import React from 'react';
import { useForm } from 'react-hook-form';
import { Container } from 'react-bootstrap';
import KaAlert from '../Common/KaAlert';
import useDisplayMessage from '../Common/useDisplayMessage';
import SpinnerButton from '../Common/SpinnerButton';
import { CenteredSpinner } from '../Common/CenteredSpinner';
import useGetFieldAlyticsSettings, { FieldAlyticsSettings } from '../../requests/fieldalytics/useGetFieldAlyticsSettings';
import useUpdateFieldAlyticsSettings from '../../requests/fieldalytics/useUpdateFieldAlyticsSettings';
import useValidateFieldAlyticsCredentials from '../../requests/fieldalytics/useValidateFieldAlyticsCredentials';
import { useUserOrKaEmployee } from '../UserAccess/useUserOrKaEmployee';
import { LoginType } from '../UserAccess/UserContext';

interface FormFields {
  fieldAlyticsUsername: string;
}

interface SettingsTableProps {
  settings: FieldAlyticsSettings;
  displayMessageSuccess: (message: string) => void;
  displayMessageFail: (message: string) => void;
  getSettings: ReturnType<typeof useGetFieldAlyticsSettings>;
  disableSave: boolean;
}

const SettingsTable = ({
  settings,
  displayMessageSuccess,
  displayMessageFail,
  getSettings,
  disableSave,
}: SettingsTableProps) => {
  const { register, handleSubmit, getValues } = useForm<FormFields>({
    defaultValues: {
      fieldAlyticsUsername: settings.fieldAlyticsUsername,
    },
  });

  const updateSettings = useUpdateFieldAlyticsSettings({
    onSuccess: () => {
      displayMessageSuccess('Saved successfully');
      getSettings.query();
    },
    onError: (e) => displayMessageFail(e.message),
  });

  const validateCredentials = useValidateFieldAlyticsCredentials({
    onSuccess: () => displayMessageSuccess('Connection successful'),
    onError: (e) => displayMessageFail(e.message),
  });

  const testConnection = () => {
    validateCredentials.request({ username: getValues('fieldAlyticsUsername') });
  };

  const submit = (form: FormFields) => {
    updateSettings.request(form);
  };

  const isLoading = updateSettings.isLoading || validateCredentials.isLoading;

  return <>
    <table className="table table-align-middle table-borderless border border-dark">
      <tbody>
        <tr>
          <th scope="row">FieldAlytics Username</th>
          <td>
            <input
              type="text"
              {...register('fieldAlyticsUsername')}
              className="border-dark form-control"
              placeholder="Enter FieldAlytics Username"
            />
          </td>
        </tr>
        <tr>
          <th scope="row"></th>
          <td>
            <SpinnerButton
              className="btn-ghost-primary"
              onClick={testConnection}
              spinning={validateCredentials.isLoading}>
              Test Connection
            </SpinnerButton>
          </td>
        </tr>
      </tbody>
    </table>
    <div className="w-100 mt-4">
      <SpinnerButton
        className="float-end w-25 btn-ghost-primary"
        onClick={handleSubmit(submit)}
        spinning={isLoading}
        disabled={disableSave}>
        Save
      </SpinnerButton>
    </div>
  </>;
};

interface SettingsViewOnlyTableProps {
  settings: FieldAlyticsSettings;
}

const SettingsViewOnlyTable = ({ settings }: SettingsViewOnlyTableProps) =>
  <table className="table table-borderless border border-dark">
    <tbody>
      <tr>
        <th scope="row">FieldAlytics Username</th>
        <td>{settings.fieldAlyticsUsername}</td>
      </tr>
    </tbody>
  </table>;

export const FieldAlyticsSettingsPage = () => {
  const { userType } = useUserOrKaEmployee();
  const canEdit = userType === LoginType.User;
  const displayMessage = useDisplayMessage();
  const getSettings = useGetFieldAlyticsSettings({
    onError: (e) => displayMessage.fail(e.message),
  });

  return <Container>
    <h2 className="mb-5 ka-blue">FieldAlytics - Settings</h2>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    {getSettings.isLoading
      ? <CenteredSpinner />
      : canEdit
        ? <SettingsTable
          key={getSettings.data?.fieldAlyticsUsername ?? '__empty__'}
          settings={getSettings.data ?? { fieldAlyticsUsername: '' }}
          displayMessageSuccess={displayMessage.success}
          displayMessageFail={displayMessage.fail}
          getSettings={getSettings}
          disableSave={!getSettings.data}
        />
        : <SettingsViewOnlyTable settings={getSettings.data ?? { fieldAlyticsUsername: '' }} />}
  </Container>;
};
